/**
 * Textos visibles de la aplicación.
 *
 * Están fuera del JSX porque cada pantalla tiene variante móvil y de escritorio
 * (ADR-0006): un texto escrito en el marcado se duplica, y las dos copias se
 * separan en cuanto alguien corrige una.
 *
 * Estilo: español neutro, directo, sin jerga técnica y sin exclamaciones. Un
 * mensaje de bloqueo dice el motivo y la acción siguiente. Los botones van en
 * infinitivo, salvo el escáner, que es ESCANEAR.
 */
export const textos = {
  app: {
    nombre: 'CQINV',
    descripcion: 'Inventario y trazabilidad de activos',
    loginTitulo: 'Entrar a CQINV',
  },
  inicio: {
    titulo: 'Cada activo se identifica al escanear su código.',
    bajada:
      'CQINV lleva el inventario de equipos, herramientas y maquinaria. Quien está en campo escanea, ve de qué activo se trata y registra lo que acaba de pasar.',
    paso1: 'Escanee el código del activo.',
    paso2: 'Vea a quién pertenece, dónde está y en qué estado.',
    paso3: 'Registre el movimiento, el alquiler o la revisión.',
    identificar: 'Identificar un activo',
    direccionPropia:
      'Si su organización le dio una dirección propia, ábrala: ahí entra directo a escanear y a su cuenta.',
  },
  acciones: {
    escanear: 'ESCANEAR',
    reintentar: 'Reintentar',
    cancelar: 'Cancelar',
    guardar: 'Guardar',
    iniciarSesion: 'Entrar con la cuenta de la organización',
    asignar: 'Asignar',
    alquilar: 'Alquilar',
    revisar: 'Revisar',
    escanearSiguiente: 'Escanear siguiente',
    entrarLocalAdmin: 'Entrar como administrador',
    entrarLocalOperario: 'Entrar como operario',
    cerrarSesion: 'Cerrar sesión',
    usarEmpresa: 'Usar',
  },
  nav: {
    inicio: 'Inicio',
    activos: 'Activos',
    operaciones: 'Operaciones',
    alquileres: 'Alquileres',
    mantenimiento: 'Mantenimiento',
    mas: 'Más',
    maestros: 'Maestros',
    tablero: 'Tablero',
    avisos: 'Avisos',
    marca: 'Marca',
    auditoria: 'Auditoría',
    importacion: 'Importación',
    almacenamiento: 'Almacenamiento',
    preferencias: 'Preferencias',
    operar: 'Operar',
    inventario: 'Inventario',
    administracion: 'Administración',
    plataforma: 'Plataforma',
    ocultarMenu: 'Ocultar menú',
    mostrarMenu: 'Mostrar menú',
  },
  plataforma: {
    titulo: 'Plataforma',
    volver: 'Volver a la aplicación',
    ayuda:
      'Cliente es quien contrata CQINV. Cada cliente tiene un administrador y una o más empresas. Las personas de la tabla son de esa empresa, no del cliente.',
    ayudaLisfas:
      'La consola de LISFAS es la del administrador de la instancia. Las personas de cada cliente se crean y se gestionan en esta pantalla.',
    abrirLisfas: 'Abrir consola de LISFAS',
    nuevoCliente: 'Nuevo cliente',
    nombreCliente: 'Nombre del cliente',
    identificador: 'Identificador',
    nuevaEmpresa: 'Nueva empresa',
    prefijo: 'Prefijo de etiquetas',
    invitar: 'Dar acceso',
    correo: 'Correo',
    rol: 'Rol',
    estado: 'Estado',
    nombre: 'Nombre',
    acciones: 'Acciones',
    empresas: 'Empresas',
    personas: 'Personas con acceso',
    clientes: 'Clientes',
    accesos: 'accesos',
    sinClientes: 'Todavía no hay clientes. Cree el primero.',
    sinEmpresas: 'Este cliente todavía no tiene empresas. Cree la primera.',
    sinSeleccion: 'Elija un cliente para ver sus empresas y las personas con acceso.',
    empresa: 'Empresa',
    creado:
      'Listo. Esa persona ya tenía cuenta en LISFAS. Se le dio acceso sin crear otra ni cambiar su contraseña.',
    cuentaCreada: 'La cuenta quedó creada en LISFAS.',
    administrador: 'Administrador del cliente',
    adminClienteAyuda:
      'Esta persona administra el cliente: crea empresas y da acceso. No es lo mismo que el administrador de una empresa.',
    sinAdministrador: 'Este cliente todavía no tiene administrador.',
    asignarAdministrador: 'Asignar administrador',
    cambiarAdministrador: 'Cambiar administrador',
    adminEmpresa: 'Administrador de la empresa',
    otroRol: 'Otro rol',
    resumeAdmin: (n: number) => (n === 1 ? '1 administrador' : `${n} administradores`),
    resumeOtros: (n: number) => (n === 1 ? '1 con otro rol' : `${n} con otro rol`),
    etiquetaCliente: 'Cliente',
    etiquetaEmpresa: 'Empresa',
    administraEsteCliente: 'Administra el cliente',
    sinAdminEnLista: 'Sin asignar',
    contrasena: 'Contraseña inicial (opcional)',
    contrasenaAyuda: 'Si la deja vacía, se genera una y se muestra una sola vez.',
    copiarContrasena: 'Copiar contraseña',
    sinPersonas: 'Nadie tiene acceso todavía. Use Dar acceso para crear la primera persona.',
    sinResultados: 'Nadie coincide con esa búsqueda.',
    buscarPersona: 'Buscar por nombre o correo',
    estados: { invited: 'Invitado', active: 'Activo', suspended: 'Suspendido', removed: 'Retirado' },
    claveUnaVez:
      'Contraseña inicial. Cópiela ahora: no se guarda en CQINV y no se vuelve a mostrar. LISFAS pedirá cambiarla al entrar.',
    ocultarClave: 'Ocultar contraseña',
    copiada: 'Contraseña copiada. Péguela en un lugar seguro y ocúltela aquí.',
    copieAMano: 'No se pudo copiar. Seleccione la contraseña y cópiela a mano.',
    adminSinEmpresa: 'Quedó como administrador del cliente. Al crear la primera empresa tendrá acceso.',
    adminAyuda: 'Queda como administrador de todas las empresas de este cliente.',
    empresaInicial: 'Primera empresa (opcional)',
    ver: 'Ver',
    editar: 'Editar',
    retirar: 'Retirar',
    retirarAcceso: 'Retirar acceso',
    ficha: 'Ficha de la persona',
    editarAcceso: 'Editar acceso',
    cerrar: 'Cerrar',
    cargando: 'Cargando clientes…',
    guardado: 'Se guardaron los cambios.',
    retirado: 'Se retiró el acceso. La cuenta sigue existiendo y puede volver a darse de alta.',
    confirmarRetirar: (nombre: string, empresa: string) =>
      `Se retira el acceso de ${nombre} a ${empresa}. La cuenta se conserva y puede volver a darse de alta.`,
    correoFijo: 'El correo identifica la cuenta y no se cambia desde aquí.',
    nombreGlobal: 'El nombre se actualiza en todas las empresas donde esta persona tiene acceso.',
    empresasDe: (n: number) => (n === 1 ? '1 empresa' : `${n} empresas`),
    personasDe: (n: number) => (n === 1 ? '1 persona' : `${n} personas`),
    inactiva: 'Inactiva',
  },
  preferencias: {
    titulo: 'Preferencias',
    tema: 'Tema',
    temaClaro: 'Claro',
    temaOscuro: 'Oscuro',
    variante: 'Variante de pantalla',
    varianteAuto: 'Según el dispositivo',
    forzarMovil: 'Forzar móvil',
    forzarEscritorio: 'Forzar escritorio',
  },
  soloEscritorio: {
    titulo: 'Esta pantalla se usa en un equipo de escritorio',
    ayuda: 'Ábrala en un ordenador para continuar. En el teléfono use ESCANEAR u Operaciones.',
  },
  busqueda: {
    abrir: 'Buscar activos (Ctrl+K)',
    placeholder: 'Token, código visible, serial o nombre',
    buscar: 'Buscar',
  },
  activos: {
    titulo: 'Activos',
    registrar: 'Registrar',
    editar: 'Editar',
    marca: 'Marca',
    modelo: 'Modelo',
    notas: 'Notas',
    serial: 'Serial',
    escanearSerial: 'Escanear serial',
    situacion: 'Situación',
    categoria: 'Categoría',
    subcategoria: 'Subcategoría',
    elegirCategoria: 'Elija una categoría',
    elegirSubcategoria: 'Elija una subcategoría',
    sinCategorias: 'Todavía no hay categorías. Escriba el nombre y pulse Crear categoría.',
    faltaCategoria: 'Cree o elija una categoría para continuar.',
    escribaNombre: 'Escriba el nombre.',
    sinPermisoCategoria: 'No tiene permiso para crear categorías. Pida a un administrador que las cree.',
    subcategoriaDespues: 'Primero cree o elija la categoría. Después puede crear la subcategoría.',
    crearCategoria: 'Crear categoría',
    crearSubcategoria: 'Crear subcategoría',
    siguiente: 'Siguiente',
    confirmarAlta: 'Revise los datos y pulse Guardar.',
    valorCompra: 'Valor de compra',
    lineaTiempo: 'Línea de tiempo',
    datos: 'Datos del activo',
    codigoInterno: 'Código interno',
    codigoVisible: 'Código visible',
    descripcion: 'Descripción',
    fechaIngreso: 'Fecha de ingreso',
    garantia: 'Garantía (meses)',
    finGarantia: 'Fin de garantía',
    vidaUtil: 'Vida útil (meses)',
    valorActual: 'Valor actual',
    valorResidual: 'Valor residual',
    responsable: 'Responsable',
    cliente: 'Cliente',
    tipoUbicacion: 'Tipo de ubicación',
    paginaAnterior: 'Hoja anterior',
    paginaSiguiente: 'Hoja siguiente',
    hoja: (n: number) => `Hoja ${n}`,
    hayMas: 'Hay más activos en la siguiente hoja.',
    valorComercial: 'Valor comercial',
    depreciacion: 'Depreciación anual (%)',
    depreciacionAyuda: 'Viene de la categoría. 10 % anual equivale a 10 años de vida útil.',
    ubicacion: 'Ubicación inicial',
    ubicacionAyuda: 'Opcional. El activo puede quedar sin bodega.',
    sinUbicacion: 'Sin ubicación',
    elegirUbicacion: 'Elija la bodega',
    todasCategorias: 'Todas las categorías',
    todasSubcategorias: 'Todas las subcategorías',
    agrupar: 'Agrupar por referencia',
    verUnidades: 'Ver unidades',
    referencia: 'Referencia',
    unidades: (n: number) => `${n} unidades`,
    corregirUbicacion: 'Corregir ubicación',
    corregirAyuda: 'Corrige la ubicación registrada. No es un traslado.',
    motivoCorreccion: 'Motivo de la corrección',
    motivoObligatorio: 'Escriba el motivo de la corrección.',
    elegirBodegaCorreccion: 'Elija la bodega correcta.',
    correccionLista: (n: number) => `Se corrigió la ubicación de ${n} activos.`,
    correccionParcial: (ok: number, fallos: number) => `Corregidos: ${ok}. Sin corregir: ${fallos}.`,
    seleccionados: (n: number) => `${n} seleccionados`,
    crearBodega: 'Crear bodega',
    sububicacion: 'Sububicación',
    elegirSububicacion: 'Sin sububicación',
    crearSububicacion: 'Crear sububicación',
    errorCarga: 'No se pudieron cargar los activos. Vuelva a intentarlo.',
    filtroActivo: 'Listado filtrado desde el tablero.',
    vacio: 'No hay activos en este listado.',
    vacioAccion: 'Registre un activo o escanee un código QR.',
    estado: 'Estado',
    filtros: 'Filtros',
    ordenar: 'Ordenar',
    acciones: 'Acciones',
    columnas: 'Columnas',
    columnasAyuda: 'Elija qué datos quiere ver en esta lista. Se guarda en este equipo.',
    columnasDefecto: 'Restablecer',
    seleccionar: 'Seleccionar',
    dejarSeleccionar: 'Dejar de seleccionar',
    continuar: 'Continuar',
    modoSeleccionAyuda: 'Marque los activos y pulse Continuar.',
    cambiarBodega: 'Cambiar de bodega',
    cambiarDesvalorizacion: 'Cambiar desvalorización',
    desvalorizacionAyuda: 'Aplica el mismo porcentaje anual a los activos seleccionados.',
    porcentajeDesvalorizacion: 'Porcentaje anual',
    faltaPorcentaje: 'Escriba un porcentaje entre 0,01 y 100.',
    edicionMasiva: 'Edición masiva',
    agruparNombre: 'Agrupar con un nombre',
    nombreGrupo: 'Nombre del grupo',
    grupo: 'Grupo',
    bodega: 'Bodega',
    todasBodegas: 'Todas las bodegas',
    precioCompra: 'Precio de compra',
    fechaCompra: 'Fecha de compra',
    proveedor: 'Proveedor',
    fabricante: 'Fabricante',
    factura: 'Factura',
    moneda: 'Moneda',
    buscar: 'Buscar',
    aplicarFiltros: 'Aplicar',
    limpiarFiltros: 'Quitar filtros',
    sinQr: 'Sin código QR',
    etiqueta: 'Etiqueta QR',
    generarEtiqueta: 'Generar etiqueta',
    imprimirEtiqueta: 'Imprimir etiqueta',
    sinEtiqueta: 'Este activo no tiene código QR. Genere la etiqueta para ver el código e imprimirlo.',
    sinPermisoEtiqueta: 'Este activo no tiene código QR. Quien administre los códigos puede generar la etiqueta.',
    errorEtiqueta: 'No se pudo generar la etiqueta. Vuelva a intentarlo.',
    errorImprimir: 'No se pudo preparar la impresión. Vuelva a intentarlo.',
    todasSituaciones: 'Todas las situaciones',
    todosEstados: 'Todos los estados',
    todasUbicaciones: 'Todas las ubicaciones',
    filtrosActivos: (n: number) => (n === 1 ? '1 filtro' : `${n} filtros`),
    ordenNombre: 'Nombre',
    ordenMarca: 'Marca',
    ordenPrecio: 'Precio de compra',
    ordenFecha: 'Fecha de compra',
    ordenSituacion: 'Situación',
    ordenBodega: 'Bodega',
    ordenAsc: 'Menor a mayor',
    ordenDesc: 'Mayor a menor',
    ordenActivoAsc: 'Ordenado de menor a mayor',
    ordenActivoDesc: 'Ordenado de mayor a menor',
    eligeActivos: 'Seleccione al menos un activo.',
    masivoAyuda: 'Los campos que deje vacíos no se modifican.',
    masivoVacio: 'Escriba al menos un campo para cambiar.',
    masivoListo: (n: number) => `Se actualizaron ${n} activos.`,
    masivoParcial: (ok: number, fallos: number) => `Actualizados: ${ok}. Sin actualizar: ${fallos}.`,
    grupoAyuda: 'Los activos seleccionados quedan juntos con este nombre, aunque no se parezcan.',
    grupoListo: (n: number) => `Se agruparon ${n} activos.`,
    faltaNombreGrupo: 'Escriba el nombre del grupo.',
    ubicacionActual: 'Ubicación',
    valor: 'Valor',
    marcaFiltro: 'Marca',
  },
  scanner: {
    tituloMovil: 'Escanear etiqueta',
    tituloEscritorio: 'Lector de códigos',
    enfoque: 'Enfoque el código QR',
    enfoqueEtiqueta: 'Enfoque el código de barras o el QR',
    linterna: 'Linterna',
    voltear: 'Cámara',
    cerrar: 'Cerrar',
    hid: 'El lector escribe aquí',
    manual: 'Código visible o token',
    noLeido: 'No se reconoció un código. Escriba el código visible.',
    noEncontrado: 'No hay un activo con ese código.',
    qrDisponible: 'Este código no está vinculado a un activo.',
    registrar: 'Registrar activo',
    vincular: 'Vincular a un activo existente',
    buscarSinQr: 'Buscar activo sin etiqueta',
    vinculado: 'Código vinculado. Escanee el siguiente.',
    codigo: 'Código',
    requiereContextoSeguro:
      'La cámara solo funciona en HTTPS o en localhost. En el teléfono use el acceso por USB o un túnel HTTPS, no la IP de la red.',
    sinCamara: 'Este navegador no ofrece cámara. Escriba el código visible.',
    camaraDenegada: 'No hay permiso de cámara. Autorícela o escriba el código visible.',
    sinDetector: 'Este navegador no lee QR con la cámara. Escriba el código visible.',
    sinLinterna: 'Esta cámara no enciende la linterna. Acerque el código o use otra luz.',
  },
  identificar: {
    pertenece: (empresa: string) => `Pertenece a ${empresa}`,
    sinEmpresa: 'Activo identificado. La empresa no publicó su nombre.',
    sinContacto: 'No hay un teléfono ni un correo publicados. Use el mensaje de arriba.',
    otro: 'Escanear otro',
    consultar: 'Consultar',
    noLeido: 'No se reconoció el código.',
    error: 'No se pudo consultar ese código. Inténtelo de nuevo.',
  },
  movimientos: {
    mover: 'Mover',
    destino: 'Destino',
    aZona: 'Enviar a zona',
    aBodega: 'Entrar a bodega',
    asignar: 'Asignar a responsable',
    entregar: 'Entregar a cliente',
    devolverLote: 'Recibir devolución',
    aMantenimiento: 'Enviar a mantenimiento',
    inspeccionar: 'Inspeccionar',
    tipoOperacion: 'Operación',
    elegirDestino: 'Elija el destino',
    elegirResponsable: 'Elija el responsable',
    elegirCliente: 'Elija el cliente',
    sesionTitulo: 'Sesión de escaneo',
    abrirSesion: 'Abrir sesión',
    empezarEscanear: 'Empezar a escanear',
    operacionActiva: 'Operación',
    leidos: (n: number, ok: number) => `${n} leídos · ${ok} válidos`,
    confirmar: 'Confirmar',
    reintentar: 'Reintentar confirmación',
    confirmando: 'Confirmando. Si no hay red, se reintenta solo.',
    retomar: (n: number) =>
      n === 1
        ? 'Tiene una sesión de 1 activo sin confirmar.'
        : `Tiene una sesión de ${n} activos sin confirmar.`,
    error: 'No se pudo registrar el movimiento. Revise el destino y vuelva a intentarlo.',
    errorRed: 'No hay red. Se reintentará la confirmación. No cierre esta pantalla.',
  },
  alquileres: {
    titulo: 'Alquileres',
    nuevo: 'Nuevo alquiler',
    entregar: 'Entregar',
    devolver: 'Devolver',
    cliente: 'Cliente',
    elegirCliente: 'Elija el cliente',
    fechaPrevista: 'Devolución prevista',
    prestamo: 'Es un préstamo',
    activos: 'Identificadores de activo, uno por línea',
    abiertos: 'Alquileres abiertos',
    estadoDevolucion: 'Estado al devolver',
    observaciones: 'Observaciones',
    foto: 'Fotografía de la devolución',
    condiciones: {
      ok: 'Sin novedad',
      damaged: 'Con daños',
      missing: 'No se encontró',
      needs_maintenance: 'Requiere mantenimiento',
    },
    error: 'No se pudo registrar el alquiler. Revise el cliente y los activos.',
  },
  auditoria: {
    titulo: 'Auditoría',
    desde: 'Desde',
    hasta: 'Hasta',
    accion: 'Acción',
    consultar: 'Consultar',
    vacio: 'No hay eventos en este rango.',
    error: 'No se pudo cargar la auditoría. Acote el rango e inténtelo de nuevo.',
  },
  fotos: {
    titulo: 'Fotografías',
    adjuntar: 'Adjuntar',
    pendiente: 'Pendiente de sincronizar',
    error: 'No se pudo guardar la fotografía. Use JPEG, PNG o WebP.',
  },
  inspeccion: {
    titulo: 'Revisión',
    sigueIgual: 'Sigue igual',
    guardar: 'Registrar revisión',
    estado: 'Estado',
    observacion: 'Observación',
    foto: 'Fotografía (opcional)',
    abrirMantenimiento: 'Abrir orden de mantenimiento',
    condiciones: {
      good: 'Bueno',
      fair: 'Regular',
      poor: 'Malo',
      out_of_service: 'Fuera de servicio',
    },
    error: 'No se pudo registrar la inspección. Vuelva a intentarlo.',
  },
  tablero: {
    titulo: 'Tablero',
    actualizado: 'Actualizado',
    refrescar: 'Actualizar',
    error: 'No se pudo cargar el tablero. Vuelva a intentarlo.',
    importar: 'Importar',
    soloEscritorio: 'La importación se hace desde un equipo de escritorio.',
    vacio: 'No hay activos registrados en esta empresa.',
    vacioAccion: 'Escanee un código QR, registre el primer activo o genere un lote.',
    registrarPrimero: 'Registrar activo',
    generarQr: 'Generar lote de QR',
    rutaInicial: 'Empiece por aquí',
  },
  importacion: {
    titulo: 'Importación',
    contenido: 'Pegue el CSV de activos',
    cargar: 'Cargar',
    validar: 'Validar',
    confirmar: 'Confirmar',
    resumen: (ok: number, avisos: number, errores: number) =>
      `Correctas: ${ok}. Advertencias: ${avisos}. Con error: ${errores}.`,
    creados: (n: number) => `Se crearon ${n} activos.`,
    error: 'No se pudo completar la importación. Revise el archivo e inténtelo de nuevo.',
  },
  marca: {
    titulo: 'Marca',
    nombre: 'Nombre de la aplicación',
    error: 'No se pudo guardar la marca. Vuelva a intentarlo.',
    mostrarPie: 'Mostrar la referencia a CQINV',
  },
  avisos: {
    titulo: 'Avisos',
    vacio: 'No hay avisos abiertos.',
  },
  pie: {
    poweredBy: 'Con tecnología de CQINV',
  },
  reportes: {
    titulo: 'Reportes',
    ejecutar: 'Consultar',
    exportar: 'Exportar',
    exportarEscritorio: 'La exportación a archivo se hace desde un equipo de escritorio.',
    error: 'No se pudo cargar el reporte. Vuelva a intentarlo.',
    enFondo: 'La exportación se está generando. Recibirá un aviso cuando esté lista.',
    campos: 'Campos del reporte',
    seleccionarTodos: 'Seleccionar todos',
    quitarTodos: 'Quitar todos',
    codigoInterno: 'Código interno',
    codigoVisible: 'Código visible',
    tipoUbicacion: 'Tipo de ubicación',
    situacionLogistica: 'Situación logística',
    estadoOperativo: 'Estado operativo',
    descripcion: 'Descripción',
    valorActual: 'Valor actual',
    valorInicial: 'Valor inicial',
    valorResidual: 'Valor residual',
    vidaUtilMeses: 'Vida útil (meses)',
    depreciacionAcumulada: 'Depreciación acumulada',
    metodoDepreciacion: 'Método de depreciación',
    fechaIngreso: 'Fecha de ingreso',
    garantiaMeses: 'Garantía (meses)',
    finGarantia: 'Fin de garantía',
    ultimaInspeccion: 'Última inspección',
    retiro: 'Retiro',
    motivoRetiro: 'Motivo de retiro',
    creadoEl: 'Creado',
    actualizadoEl: 'Actualizado',
    horizontal: 'Vista horizontal: hay muchos campos.',
    vertical: 'Vista vertical: hay pocos campos y el texto es largo.',
    sinFilas: 'La consulta no devolvió filas.',
    elijaCampo: 'Seleccione al menos un campo.',
    imprimir: 'Imprimir',
    hayMas: 'Hay más filas. Exporte el archivo para ver el conjunto completo.',
  },
  mantenimiento: {
    titulo: 'Mantenimiento',
    activo: 'Identificador del activo',
    problema: 'Problema',
    enviar: 'Enviar a mantenimiento',
    finalizar: 'Finalizar',
    destino: 'Bodega de destino al finalizar',
    resultado: 'Resultado',
    retirar: 'Retirar el activo',
    resultados: {
      repaired: 'Reparado',
      partially_repaired: 'Reparación parcial',
      not_repairable: 'No reparable',
      no_fault_found: 'Sin falla',
    },
    error: 'No se pudo registrar el mantenimiento. Revise el activo y vuelva a intentarlo.',
  },
  medidores: {
    titulo: 'Medidores',
    lectura: 'Nueva lectura',
    registrar: 'Registrar lectura',
    nombre: 'Nombre',
    unidad: 'Unidad',
    crear: 'Añadir medidor',
    vacio: 'Este activo no tiene medidores.',
    error: 'No se pudo registrar la lectura. Compruebe que no sea menor que la anterior.',
  },
  almacenamiento: {
    titulo: 'Almacenamiento',
    ayuda:
      'Las fotografías y documentos se guardan en el disco de este servidor. Las credenciales nunca se muestran.',
    nombre: 'Nombre',
    predeterminado: 'Predeterminado',
    vacio: 'No hay un destino configurado. Guarde el disco local para empezar.',
    error: 'No se pudo cargar el almacenamiento. Vuelva a intentarlo.',
  },
  pwa: {
    nuevaVersion: 'Hay una versión nueva. Pulse Actualizar cuando no esté escaneando.',
    actualizar: 'Actualizar',
  },
  maestros: {
    categorias: 'Categorías',
    bodegas: 'Bodegas',
    zonas: 'Zonas de trabajo',
    clientes: 'Clientes',
    responsables: 'Responsables',
    nombre: 'Nombre',
    codigo: 'Código',
    subcategoria: 'subcategoría',
    subcategoriaTitulo: 'Subcategoría',
    depreciacion: 'Depreciación anual (%)',
    padre: 'Categoría padre (opcional)',
    errorCarga: 'No se pudieron cargar los maestros. Vuelva a intentarlo.',
    soloEscritorio: 'La configuración de maestros está disponible en un equipo de escritorio.',
    campos: 'Campos personalizados',
    clave: 'Clave (snake_case)',
    tipo: 'Tipo',
    obligatorio: 'Obligatorio',
    opciones: 'Opciones, separadas por coma',
    elegirOpcion: 'Elija una opción',
    editar: 'Editar',
    eliminar: 'Eliminar',
    desactivar: 'Desactivar',
    activar: 'Activar',
    nuevo: 'Nuevo',
    categoria: 'Categoría',
    estado: 'Estado',
    enUso: 'En uso',
    inactiva: 'Fuera de uso',
    padreEtiqueta: 'Categoría padre',
    sinPadre: 'Ninguna',
    sinPorcentaje: 'Sin porcentaje',
    subcategorias: 'Subcategorías',
    sinSubcategorias: 'Esta categoría no tiene subcategorías.',
    subcategoriaDe: (nombre: string) => `Subcategoría de ${nombre}`,
    cuentaSubcategorias: (n: number) => (n === 1 ? '1 subcategoría' : `${n} subcategorías`),
    sinCampos: 'Esta categoría no tiene campos personalizados.',
    heredados: 'Heredados de la categoría padre',
    propios: 'De esta categoría',
    ubicaciones: 'Sububicaciones',
    sinUbicaciones: 'Esta bodega no tiene sububicaciones.',
    dosNiveles: 'Sólo se admiten dos niveles. Mueva o elimine las subcategorías antes de asignar un padre.',
    padreInvalido: 'Elija una categoría raíz como padre.',
    depreciacionInvalida: 'Escriba un porcentaje entre 0,01 y 100, o déjelo vacío.',
    codigoInvalido: 'El código usa letras, números, punto, guion o guion bajo.',
    faltaNombre: 'Escriba el nombre.',
    cargando: 'Cargando los catálogos.',
    cargandoDetalle: 'Cargando el detalle.',
    guardado: 'Cambios guardados.',
    quedoFueraDeUso: 'Quedó fuera de uso. Puede activarlo de nuevo desde esta fila.',
    quedoEnUso: 'Volvió a estar disponible.',
    confirmarEliminar: (nombre: string) =>
      `¿Eliminar ${nombre}? Si no tiene activos ni subcategorías, quedará inactiva.`,
    confirmarDesactivar: (nombre: string) =>
      `¿Desactivar ${nombre}? Dejará de ofrecerse en los formularios nuevos.`,
    vacio: {
      categorias: 'No hay categorías.',
      bodegas: 'No hay bodegas.',
      zonas: 'No hay zonas de trabajo.',
      clientes: 'No hay clientes.',
      responsables: 'No hay responsables.',
    },
    tiposPersona: {
      legal: 'Persona jurídica',
      natural: 'Persona natural',
    },
    tiposResponsable: {
      employee: 'Empleado',
      contractor: 'Contratista',
      internal: 'Interno',
    },
    tiposZona: {
      project: 'Proyecto',
      site: 'Obra',
      workshop: 'Taller',
      plant: 'Planta',
      office: 'Oficina',
      other: 'Otra',
    },
    tiposExtra: {
      number: 'Número',
      decimal: 'Decimal',
      multiselect: 'Lista múltiple',
    },
    tipos: {
      text: 'Texto',
      integer: 'Entero',
      date: 'Fecha',
      boolean: 'Sí o no',
      select: 'Lista',
    },
    direccion: 'Dirección',
    latitud: 'Latitud',
    longitud: 'Longitud',
    verEnMapa: 'Ver en el mapa',
    punto: 'Punto',
    jefeDirecto: 'Jefe directo',
    sinJefe: 'Sin jefe directo',
    puestosACargo: 'Puestos a cargo',
    personasACargo: 'Personas a cargo',
    sinPersonas: 'Nadie reporta a esta persona.',
    coordenadasInvalidas: 'Escriba latitud y longitud válidas, o deje ambas vacías.',
    puestosInvalidos: 'Escriba un número entero de puestos a cargo, o déjelo vacío.',
  },
  qr: {
    lotes: 'Lotes de QR',
    ayuda: 'Escriba cuántos códigos necesita. El sistema numera en secuencia para que no se repitan.',
    cantidad: 'Cantidad',
    siguiente: (codigo: string) => `El siguiente código será ${codigo}.`,
    loteResumen: (cantidad: number, desde: string, hasta: string) =>
      `${cantidad} códigos · ${desde} a ${hasta}`,
    estados: {
      ready: 'Listo',
      generating: 'Generando',
      failed: 'No se pudo generar',
    },
    desde: 'Desde',
    hasta: 'Hasta',
    generar: 'Generar',
    exportar: 'Exportar CSV',
    exportarExcel: 'Exportar Excel',
    imprimir: 'Imprimir PDF',
    errorCarga: 'No se pudieron cargar los lotes. Vuelva a intentarlo.',
    politica: 'Ficha pública del QR',
    politicaAyuda: 'Texto y datos que ve quien escanea el código sin haber iniciado sesión.',
    habilitada: 'Mostrar ficha pública',
    mostrarEmpresa: 'Mostrar el nombre de la empresa',
    mostrarActivo: 'Mostrar el nombre del activo',
    mostrarCodigo: 'Mostrar el código visible',
    mostrarTelefono: 'Mostrar teléfono',
    telefono: 'Teléfono',
    mostrarCorreo: 'Mostrar correo',
    correo: 'Correo',
    mensajeDevolucion: 'Mensaje de devolución',
    sitio: 'Sitio web (https)',
    errorPolitica: 'No se pudo guardar la ficha pública. Revise los campos e inténtelo de nuevo.',
  },
  sesion: {
    comprobando: 'Comprobando la sesión',
    sinSesion: 'Inicie sesión para continuar',
    localAviso: 'Use una cuenta local de prueba. No hace falta correo.',
    sinEmpresa: 'Seleccione la empresa con la que va a trabajar',
    soloPlataforma: (correo: string | null) =>
      correo
        ? `${correo} administra la plataforma y no tiene acceso a ninguna empresa. Por eso no entra al inventario. En el panel, use Dar acceso con este mismo correo en la empresa que quiera usar.`
        : 'Esta cuenta administra la plataforma y no tiene acceso a ninguna empresa. En el panel, use Dar acceso con este mismo correo en la empresa que quiera usar.',
    irAlPanel: 'Volver al panel de plataforma',
    sinPermiso: 'No tiene permiso para esta acción. Elija otra empresa o pida acceso.',
    error: 'No se pudo comprobar la sesión. Vuelva a intentarlo.',
    listo: (nombre: string, empresa: string) => `${nombre} · ${empresa}`,
    cambiarEmpresa: 'Empresa',
  },
  estado: {
    comprobando: 'Comprobando la conexión con el servidor',
    conectado: 'Servidor disponible',
    sinConexion: 'Sin conexión con el servidor',
  },
  dispositivo: {
    movil: 'Variante móvil',
    escritorio: 'Variante de escritorio',
  },
} as const;
